import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { EMAIL_AUTH_URL } from '../config';
import type { AppUser } from '../types'; // Import shared AppUser type

const SetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');

  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');
  const resetToken = searchParams.get('resetToken'); // For password reset flow

  useEffect(() => {
    if (!token && !resetToken) {
      setError('Invalid or missing verification token');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [token, resetToken, emailParam, navigate]);

  const handleSetPassword = async () => {
    setError(null);
    setSuccess(null);

    // Validate passwords
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      let response;
      
      if (resetToken) {
        // Password reset flow
        response = await axios.post(`${EMAIL_AUTH_URL}/reset-password`, {
          token: resetToken,
          password
        });
      } else {
        // Initial password set after email verification
        response = await axios.post(`${EMAIL_AUTH_URL}/set-password`, 
          { password },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data.token) {
        localStorage.setItem('unitaiAuthToken', response.data.token);
        localStorage.setItem('unitaiUser', JSON.stringify(response.data.user));
      }

      setSuccess('Password set successfully! Redirecting to dashboard...');
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/', { 
          state: { 
            loginSuccess: true, 
            user: response.data.user 
          } 
        });
      }, 2000);
      
    } catch (error: any) {
      console.error('Set password failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data?.message || 'Failed to set password');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {resetToken ? 'Reset Your Password' : 'Set Your Password'}
          </Typography>

          {email && (
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
              for {email}
            </Typography>
          )}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSetPassword(); }}>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              margin="normal"
              required
              autoFocus
              helperText="Minimum 6 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, height: 48 }}
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? <CircularProgress size={24} /> : 'Set Password'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={() => navigate('/')}
                disabled={loading}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SetPasswordPage;
